/**
 * ChartProps Implementation Pattern
 * Standardized pattern for implementing ChartProps interface across all charts
 * Ensures consistent data fetching, loading states, and parameter correction application
 *
 * ENHANCED: Now includes React Query caching for better performance
 */

import React, { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3 } from 'lucide-react';
import {
  calculatePowerCorrections,
  getCorrectionDisplayText,
  ParameterInfo,
} from './parameterCorrections';

/**
 * Standard ChartProps interface - all charts should implement this
 */
export interface ChartProps {
  sessionId: string;
  parameterInfo?: ParameterInfo | null;
  className?: string;
}

/**
 * Enhanced hook for managing chart state, parameter corrections, and Chart.js features
 * Supports advanced features like decimation, zoom, performance optimizations
 *
 * NEW: React Query caching for intelligent data management
 */
export function useChartPropsPattern<T>(
  sessionId: string,
  parameterInfo: ParameterInfo | null,
  dataFetcher: (sessionId: string) => Promise<T>,
  options?: {
    enablePerformanceMode?: boolean;
    dataValidation?: (data: T) => T;
    retryAttempts?: number;
    // NEW: React Query options
    queryKey?: string[];
    staleTime?: number;
    gcTime?: number;
  }
) {
  // Generate unique query key for this chart + data type
  const queryKey = useMemo(() => {
    const baseKey = options?.queryKey || ['chart-data'];
    // Use parameterInfo.id if available, otherwise use a hash of the parameterInfo object
    const parameterId =
      parameterInfo?.id || (parameterInfo ? JSON.stringify(parameterInfo).slice(0, 20) : 'default');
    return [...baseKey, sessionId, parameterId];
  }, [sessionId, parameterInfo, options?.queryKey]);

  // React Query for intelligent caching
  const {
    data: queryData,
    isLoading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => dataFetcher(sessionId),
    enabled: !!sessionId,
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes default
    gcTime: options?.gcTime ?? 10 * 60 * 1000, // 10 minutes default
    refetchOnWindowFocus: false, // Better UX for dashboard
    refetchOnMount: false, // Use cached data if available
    retry: (failureCount, error: any) => {
      const maxRetries = options?.retryAttempts ?? 3;
      // Don't retry 4xx errors (client errors)
      if (error?.status >= 400 && error?.status < 500) return false;
      // Don't retry 401 (unauthorized)
      if (error?.status === 401) return false;
      // Don't retry if the data fetcher returned null (that's valid, not an error)
      if (error?.message?.includes('null') || error?.message?.includes('No data')) return false;
      return failureCount < maxRetries;
    },
  });

  // Parameter corrections
  const corrections = useMemo(() => calculatePowerCorrections(parameterInfo), [parameterInfo]);

  const correctionDisplay = useMemo(() => getCorrectionDisplayText(corrections), [corrections]);

  // Retry function for manual retry
  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  // Transform query error to string for compatibility
  const error = useMemo(() => {
    if (!queryError) return null;
    if (queryError instanceof Error) return queryError.message;
    if (typeof queryError === 'string') return queryError;
    return 'Failed to load data';
  }, [queryError]);

  return {
    data: queryData,
    loading: queryLoading,
    error,
    corrections,
    correctionDisplay,
    isParameterEnhanced: corrections.isEnhanced,
    correctionFactor: corrections.correction,
    retry,
    // Chart.js feature helpers
    isLargeDataset: Array.isArray(queryData) && queryData.length > 1000,
    shouldUseDecimation:
      options?.enablePerformanceMode && Array.isArray(queryData) && queryData.length > 1000,
    // NEW: React Query specific helpers
    isStale: false, // Always false since we control staleTime
    isFetching: false, // Not currently fetching
    isSuccess: !!queryData && !queryError,
    isError: !!queryError,
  };
}

/**
 * Standard component wrapper for parameter enhancement indicators
 */
export const ParameterEnhancementBadge: React.FC<{
  correctionDisplay: ReturnType<typeof getCorrectionDisplayText>;
}> = ({ correctionDisplay }) => {
  if (!correctionDisplay) return null;

  return (
    <div className="mb-4 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <h4 className="text-sm font-medium text-blue-900 mb-2">{correctionDisplay.title}</h4>
      <div className="text-sm text-blue-700 leading-relaxed">
        {correctionDisplay.description} {correctionDisplay.factor}
      </div>
    </div>
  );
};

/**
 * Standard loading component for charts
 */
export const ChartLoadingState: React.FC<{ message?: string }> = ({
  message = 'Loading chart data...',
}) => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
      <p className="text-gray-600 text-sm">{message}</p>
    </div>
  </div>
);

/**
 * Enhanced error component for charts with retry functionality
 */
export const ChartErrorState: React.FC<{
  error: string;
  onRetry?: () => void;
  retryCount?: number;
  maxRetries?: number;
}> = ({ error, onRetry, retryCount = 0, maxRetries = 3 }) => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center max-w-md px-4">
      <div className="text-red-500 mb-3 text-2xl">⚠️</div>
      <p className="text-gray-700 text-sm mb-3 font-medium">Chart Loading Error</p>
      <p className="text-gray-600 text-xs mb-4 leading-relaxed">{error}</p>

      {onRetry && retryCount < maxRetries && (
        <div className="space-y-2">
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
          >
            Retry Loading Data
          </button>
          {retryCount > 0 && (
            <p className="text-xs text-gray-500">
              Attempt {retryCount + 1} of {maxRetries + 1}
            </p>
          )}
        </div>
      )}

      {retryCount >= maxRetries && (
        <p className="text-xs text-red-600">
          Unable to load data after multiple attempts. Please check your connection or contact
          support.
        </p>
      )}
    </div>
  </div>
);

/**
 * Standard empty state component for charts
 */
export const ChartEmptyState: React.FC<{ message?: string }> = ({
  message = 'Session data will be displayed here once uploaded',
}) => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center">
      <div className="text-gray-400 mb-2">
        <BarChart3 className="h-8 w-8 mx-auto" />
      </div>
      <p className="text-gray-600 text-sm">{message}</p>
    </div>
  </div>
);

// Alias for backward compatibility with tests
export const NoDataMessage = ChartEmptyState;

/**
 * Flexible chart container wrapper with optional title and parameter enhancement indicator
 * Can be used as a full wrapper or just for parameter enhancement display
 */
export const ChartContainer: React.FC<{
  title?: string;
  children: React.ReactNode;
  className?: string;
  correctionDisplay?: ReturnType<typeof getCorrectionDisplayText>;
  showTitle?: boolean;
  customHeader?: React.ReactNode;
  wrapperStyle?: 'card' | 'minimal' | 'none';
}> = ({
  title,
  children,
  className = '',
  correctionDisplay,
  showTitle = true,
  customHeader,
  wrapperStyle = 'card',
}) => {
  const content = (
    <>
      {/* Header section - optional */}
      {customHeader ||
        (showTitle && title && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {title}
              {correctionDisplay && (
                <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                  Parameter Enhanced
                </span>
              )}
            </h3>
          </div>
        ))}

      {/* Parameter enhancement badge - always shown if available */}
      <ParameterEnhancementBadge correctionDisplay={correctionDisplay} />

      {/* Chart content */}
      {children}
    </>
  );

  // Apply wrapper styling based on wrapperStyle
  switch (wrapperStyle) {
    case 'card':
      return (
        <div className={`bg-white rounded-lg shadow-sm border p-6 ${className}`}>{content}</div>
      );
    case 'minimal':
      return <div className={`${className}`}>{content}</div>;
    case 'none':
      return <>{content}</>;
    default:
      return (
        <div className={`bg-white rounded-lg shadow-sm border p-6 ${className}`}>{content}</div>
      );
  }
};
